import { useNavigate } from '@tanstack/react-router';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Tag from 'src/modules/shared/components/atoms/tag';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { PiCopy, PiPencilSimple, PiTrash } from 'react-icons/pi';
import { Route as CatalogPropertiesRoute } from 'src/routes/catalog/properties.index';
import { propertiesSearchSchema } from 'src/modules/catalog/schemas/properties-search.schema';
import {
  ReportCoverageMode,
  type ReportDashboardWidgetInstance,
} from 'src/api/generated';
import {
  type DashboardWidgetKind,
  getWidgetSetupIssues,
  getWidgetTitleLabel,
  hasRequiredWidgetFilters,
} from './reports-dashboard.utils';
import { WidgetWarningState } from './widget-warning-state';
import { WidgetQueryRenderer } from './widget-query-renderer';
import { useReportWidgetQuery } from '../hooks/reports-dashboard';
import {
  formatReportDateRangeLabel,
  isReportWidgetQueryable,
} from '../utils/report-dashboard';
import { getReportWidgetAccentColorOption } from './report-widget-accent-color';
import { ReportWidgetBodySkeleton } from './reports-dashboard-skeleton';

const ReportDashboardWidgetActions: React.FC<{
  hidden: boolean;
  dragHandle?: React.ReactNode;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
}> = ({ hidden, dragHandle, onEdit, onDuplicate, onRemove }) => {
  if (hidden) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      {dragHandle}
      <Button size="xs" appearance="ghost" variant="secondary" onClick={onEdit}>
        <PiPencilSimple className="h-4 w-4" />
      </Button>
      <Button
        size="xs"
        appearance="ghost"
        variant="secondary"
        onClick={onDuplicate}
      >
        <PiCopy className="h-4 w-4" />
      </Button>
      <Button size="xs" appearance="ghost" variant="danger" onClick={onRemove}>
        <PiTrash className="h-4 w-4" />
      </Button>
    </div>
  );
};

const ReportDashboardWidgetBody: React.FC<{
  widget: ReportDashboardWidgetInstance;
  isPreview: boolean;
  previewEnabled: boolean;
  readOnly: boolean;
  onEdit?: () => void;
  onOpenProperty: (propertyId: number) => void;
  widgetQuery: ReturnType<typeof useReportWidgetQuery>;
}> = ({
  widget,
  isPreview,
  previewEnabled,
  readOnly,
  onEdit,
  onOpenProperty,
  widgetQuery,
}) => {
  const { t } = useI18n();

  if (widget.validation?.isValid === false && !isPreview) {
    return (
      <WidgetWarningState
        title={t('reports.dashboard.validation.invalidTitle')}
        issues={getWidgetSetupIssues(widget, t)}
        onAction={readOnly ? undefined : onEdit}
        actionLabel={
          readOnly
            ? undefined
            : t('reports.dashboard.actions.editConfiguration')
        }
      />
    );
  }

  if (!previewEnabled) {
    return (
      <WidgetWarningState
        title={t('reports.dashboard.validation.incompleteTitle')}
        issues={getWidgetSetupIssues(widget, t)}
      />
    );
  }

  if (widgetQuery.loading) {
    return <ReportWidgetBodySkeleton />;
  }

  if (widgetQuery.error) {
    return (
      <ErrorState
        size="sm"
        title={t('reports.dashboard.errors.widgetLoadTitle')}
        description={t('reports.dashboard.errors.widgetLoadDescription')}
        onAction={() => widgetQuery.refetch()}
      />
    );
  }

  return (
    <WidgetQueryRenderer
      widget={widget}
      response={widgetQuery.data}
      onOpenProperty={onOpenProperty}
    />
  );
};

/**
 * Dashboard card wrapper that loads and renders a single widget.
 */
export const ReportDashboardWidgetCard: React.FC<{
  widget: ReportDashboardWidgetInstance;
  widgetTitleByKind: Map<DashboardWidgetKind, string>;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
  dragHandle?: React.ReactNode;
  readOnly?: boolean;
  isPreview?: boolean;
  previewEnabled?: boolean;
}> = ({
  widget,
  widgetTitleByKind,
  onEdit,
  onDuplicate,
  onRemove,
  dragHandle,
  readOnly = false,
  isPreview = false,
  previewEnabled = true,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const queryEnabled =
    previewEnabled &&
    isReportWidgetQueryable(widget.validation) &&
    hasRequiredWidgetFilters(widget);
  const widgetQuery = useReportWidgetQuery(widget, queryEnabled);

  const coverageNotes = widgetQuery.data?.coverage.notes ?? [];
  const widgetLabel = widget.label?.trim();
  const widgetAccentColor = getReportWidgetAccentColorOption(widget.color);

  const openProperty = (propertyId: number) => {
    void navigate({
      to: CatalogPropertiesRoute.to,
      search: () =>
        propertiesSearchSchema.parse({
          search: String(propertyId),
          activeStatus: 'ALL',
        }),
    });
  };

  return (
    <Card
      appearance="outline"
      variant="neutral"
      shadow="md"
      className={classes(
        'h-full overflow-hidden shadow-md',
        widgetAccentColor.surfaceClassName
      )}
    >
      <CardHeader
        className={classes('px-4 py-3', widgetAccentColor.headerClassName)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            {widgetLabel ? (
              <Tag
                content={widgetLabel}
                size="sm"
                variant="neutral"
                appearance="ghost"
                className={classes(
                  'w-fit border px-2.5 py-1 shadow-sm',
                  widgetAccentColor.badgeClassName
                )}
                textClassName="text-xs font-semibold"
              />
            ) : null}
            <Text
              as="h3"
              size="small"
              weight="semibold"
              className="text-primary-900"
            >
              {widget.title ??
                getWidgetTitleLabel(
                  widget.kind,
                  widgetTitleByKind,
                  t('reports.dashboard.widgetFallbackTitle')
                )}
            </Text>
            <Text as="p" size="tiny" className="text-primary-900">
              {formatReportDateRangeLabel(widget.filters.dateRange, t)}
            </Text>
          </div>
          <ReportDashboardWidgetActions
            hidden={isPreview || readOnly}
            dragHandle={dragHandle}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
          />
        </div>
      </CardHeader>
      <CardBody className="space-y-3 px-4 py-4">
        <ReportDashboardWidgetBody
          widget={widget}
          isPreview={isPreview}
          previewEnabled={previewEnabled}
          readOnly={readOnly}
          onEdit={onEdit}
          onOpenProperty={openProperty}
          widgetQuery={widgetQuery}
        />

        {widgetQuery.data?.coverage.mode !== ReportCoverageMode.EXACT &&
          coverageNotes.length > 0 && (
            <Alert
              variant="secondary"
              appearance="soft"
              size="sm"
              message={<Text size="tiny">{coverageNotes.join(' ')}</Text>}
            />
          )}
      </CardBody>
    </Card>
  );
};
